import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Mail, Sparkles, ArrowRight, Wand2, Send, ChevronLeft, Palette,
  Loader2, CheckCircle2, AlertCircle, RefreshCcw, Lock,
  Newspaper, BookOpen, LayoutGrid,
  Users, Image as ImageIcon, Edit3, MousePointerClick, CalendarClock,
  Globe, Upload, Camera,
  BarChart3, FileText, MessageSquareQuote
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = "ssemail_demo_v1";

const THEME_OPTIONS = [
  { id: "modern", label: "Modern" },
  { id: "minimal", label: "Minimal" },
  { id: "bold", label: "Bold" },
  { id: "corporate", label: "Corporate" },
  { id: "elegant", label: "Elegant" },
  { id: "real_estate", label: "Real Estate", locked: true },
  { id: "restaurant", label: "Restaurant", locked: true },
  { id: "healthcare", label: "Healthcare", locked: true },
];

const LAYOUT_OPTIONS = [
  { id: "magazine", label: "Magazine", icon: Newspaper, desc: "Zig-zag editorial feel" },
  { id: "story", label: "Story", icon: BookOpen, desc: "Long-form, photo-led", locked: true },
  { id: "cards", label: "Cards", icon: LayoutGrid, desc: "Clean stacked cards", locked: true },
];

const IMAGE_OPTIONS = [
  { id: "auto",     label: "Auto from page",       icon: Globe,  desc: "Pulled from your URL" },
  { id: "upload",   label: "Upload your own",      icon: Upload, desc: "Drag in your photos", locked: true },
  { id: "stock",    label: "Stock photo library",  icon: Camera, desc: "Browse curated photos", locked: true },
];

const SAMPLE_URLS = [
  { label: "Anne Flinn Advertising", url: "https://anneflinn.com" },
];

export default function TryPage() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { subject, html_content, template_settings, scraped_data, quota }
  const [quota, setQuota] = useState({ used: 0, remaining: 3, limit: 3 });
  const [theme, setTheme] = useState("modern");
  const [layout, setLayout] = useState("magazine");
  const [accentColor, setAccentColor] = useState("#0d9488");
  const [rerendering, setRerendering] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const rerenderTimer = useRef(null);

  // Load saved demo + quota on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setResult(parsed);
        setTheme(parsed.template_settings?.theme || "modern");
        setLayout(parsed.template_settings?.layout || "magazine");
        setAccentColor(parsed.template_settings?.accent_color || "#0d9488");
        setUrl(parsed.template_settings?.cta_url || "");
      } catch {/* ignore */}
    }
    refreshQuota();
  }, []);

  const refreshQuota = async () => {
    try {
      const res = await axios.get(`${API}/demo/quota`);
      setQuota(res.data);
    } catch {/* ignore */}
  };

  const handleGenerate = async (e) => {
    e?.preventDefault();
    setError(null);
    if (!url.trim()) {
      setError("Paste a URL first — any blog post, news article, or company page");
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/demo/generate`, { url: url.trim() });
      const data = res.data;
      setResult(data);
      setTheme(data.template_settings?.theme || "modern");
      setLayout(data.template_settings?.layout || "magazine");
      setAccentColor(data.template_settings?.accent_color || "#0d9488");
      setQuota(data.quota || quota);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      toast.success("Your newsletter is ready!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        const msg = typeof detail === "object" ? detail.message : "You've used today's free tries.";
        setError(msg);
        if (typeof detail === "object" && detail.quota) {
          setQuota((q) => ({ ...q, used: detail.quota.used, remaining: 0 }));
        }
      } else {
        setError(typeof detail === "string" ? detail : "Something went wrong. Try again with a different URL.");
      }
    } finally {
      setGenerating(false);
    }
  };

  // Re-render the email locally when user tweaks theme/color (no AI call, no quota cost)
  const triggerRerender = (newTheme, newColor, newLayout) => {
    if (!result) return;
    if (rerenderTimer.current) clearTimeout(rerenderTimer.current);
    rerenderTimer.current = setTimeout(async () => {
      setRerendering(true);
      try {
        const newSettings = {
          ...result.template_settings,
          theme: newTheme,
          accent_color: newColor,
          layout: newLayout || result.template_settings?.layout || "magazine",
        };
        const res = await axios.post(`${API}/demo/rerender`, {
          template_settings: newSettings,
          scraped_data: result.scraped_data,
        });
        const updated = {
          ...result,
          subject: res.data.subject,
          html_content: res.data.html_content,
          template_settings: newSettings,
        };
        setResult(updated);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch {/* ignore */}
      finally {
        setRerendering(false);
      }
    }, 250);
  };

  const handleSendTest = async () => {
    if (!testEmail || !/^[^@]+@[^@]+\.[^@]+$/.test(testEmail)) {
      toast.error("Please enter a valid email");
      return;
    }
    setSendingTest(true);
    try {
      await axios.post(`${API}/demo/send-test`, {
        email: testEmail,
        subject: result.subject,
        html_content: result.html_content,
      });
      toast.success(`Sent! Check your inbox at ${testEmail}`);
      setShowSendModal(false);
      setTestEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send right now");
    } finally {
      setSendingTest(false);
    }
  };

  const resetDemo = () => {
    localStorage.removeItem(STORAGE_KEY);
    setResult(null);
    setUrl("");
    setError(null);
  };

  return (
    <div className="min-h-screen bg-slate-50 relative" data-testid="try-page" style={{
      backgroundImage: "radial-gradient(circle, #cbd5e1 1px, transparent 1px)",
      backgroundSize: "24px 24px",
    }}>
      {/* DEMO MODE banner — sticky, highly visible */}
      <div className="sticky top-0 z-[60] bg-[#B7EB1B] text-[#0a1e3a] border-b-2 border-[#0a1e3a]/10" data-testid="try-demo-banner">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-2 text-sm font-bold tracking-wide text-center">
          <Sparkles className="w-4 h-4 flex-shrink-0" />
          <span className="hidden sm:inline">FREE DEMO MODE</span>
          <span className="sm:hidden">DEMO MODE</span>
          <span className="font-normal text-[#0a1e3a]/70 hidden sm:inline">·</span>
          <span className="font-normal text-[#0a1e3a]/80 text-xs sm:text-sm">
            Preview only — sign up to send to your contacts
          </span>
        </div>
      </div>

      {/* Top brand nav */}
      <nav className="sticky top-[36px] sm:top-[40px] z-50 bg-[#003858] text-white shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="try-nav-logo">
            <img src="/brand/afa-logo-pin.png" alt="Anne Flinn Advertising" className="w-10 h-10 object-contain" />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-bold tracking-wide" style={{fontFamily: 'Manrope'}}>ANNE FLINN ADVERTISING</span>
              <span className="text-xs font-medium text-[#B7EB1B]">Advertise Smarter with Us</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/simply-smarter-email" className="hidden sm:inline-flex items-center text-sm text-white/70 hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-1" /> Learn more
            </Link>
            <Button
              data-testid="try-nav-signin"
              variant="ghost"
              onClick={() => navigate("/login")}
              className="text-white/80 hover:text-white hover:bg-white/10"
            >
              Sign In
            </Button>
            <Button
              data-testid="try-nav-cta"
              onClick={() => navigate("/login")}
              className="bg-[#B7EB1B] hover:bg-[#B7EB1B]/90 text-[#0a1e3a] rounded-full font-semibold"
            >
              Start Free Trial
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero / URL input */}
      <section className="relative bg-gradient-to-br from-[#003858] via-[#006080] to-[#119AB3] text-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#B7EB1B]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#119AB3]/20 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 py-14 lg:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-[#B7EB1B] text-[#0a1e3a] font-bold px-4 py-2 rounded-full mb-5 shadow-lg shadow-[#B7EB1B]/30">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm tracking-wide">FREE LIVE DEMO</span>
            <span className="w-1 h-1 bg-[#0a1e3a]/40 rounded-full"></span>
            <span className="text-xs font-semibold">3 generations/day · No signup</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4" style={{fontFamily: 'Manrope'}}>
            Try it now. <br className="sm:hidden" />
            <span className="text-[#B7EB1B]">No strings.</span>
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-3">
            Paste any URL — we'll write and design a newsletter in 30 seconds.
          </p>
          <p className="text-sm text-[#B7EB1B]/90 max-w-2xl mx-auto mb-8 font-medium">
            <Lock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
            Demo only — to send to your real contact list, start the 14-day free trial.
          </p>

          <form onSubmit={handleGenerate} className="max-w-2xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-2 bg-white rounded-2xl p-2 shadow-2xl">
              <Input
                data-testid="try-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://yourblog.com/your-post"
                className="border-0 h-12 text-slate-900 text-base focus-visible:ring-0"
                disabled={generating}
              />
              <Button
                data-testid="try-generate-btn"
                type="submit"
                disabled={generating || quota.remaining <= 0}
                className="bg-[#B7EB1B] hover:bg-[#B7EB1B]/90 text-[#0a1e3a] rounded-xl font-bold text-base h-12 px-6 whitespace-nowrap"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Wand2 className="w-4 h-4 mr-2" /> Generate</>
                )}
              </Button>
            </div>

            {error && (
              <div className="mt-3 bg-amber-500/20 border border-amber-300/40 text-amber-100 rounded-xl px-4 py-3 flex items-start gap-2 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm text-white/60">
              <span>Try with:</span>
              {SAMPLE_URLS.map((s) => (
                <button
                  key={s.url}
                  type="button"
                  onClick={() => setUrl(s.url)}
                  className="underline hover:text-white"
                  data-testid={`try-sample-${s.url}`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="mt-6 inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-[#B7EB1B]/30 rounded-full px-4 py-1.5 text-xs text-white" data-testid="try-quota-chip">
              <Sparkles className="w-3 h-3 text-[#B7EB1B]" />
              <span className="font-semibold">Free demo:</span>
              <span className="text-white/80">{quota.remaining} of {quota.limit} generations remaining today</span>
            </div>
          </form>
        </div>
      </section>

      {/* Result section */}
      {generating && !result && (
        <section className="bg-white py-20 text-center">
          <Loader2 className="w-12 h-12 text-[#0d9488] animate-spin mx-auto mb-4" />
          <p className="text-slate-600 text-lg">Reading your webpage... drafting copy... designing layout...</p>
          <p className="text-slate-400 text-sm mt-2">This usually takes 15-30 seconds.</p>
        </section>
      )}

      {result && (
        <section className="py-10 lg:py-14" data-testid="try-result">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Customize sidebar */}
              <aside className="lg:col-span-1 space-y-5">
                <Card className="rounded-2xl border-slate-200">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <Palette className="w-5 h-5 text-[#0d9488]" />
                      <h3 className="font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Customize</h3>
                      {rerendering && <Loader2 className="w-3 h-3 animate-spin text-slate-400 ml-auto" />}
                    </div>

                    {/* Images */}
                    <div className="mb-5">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block">Images</Label>
                        <span className="text-[10px] text-slate-400 font-medium">1 free · 2 with subscription</span>
                      </div>
                      <div className="space-y-2">
                        {IMAGE_OPTIONS.map((opt) => {
                          const Icon = opt.icon;
                          const isActive = !opt.locked && opt.id === "auto";
                          return (
                            <button
                              key={opt.id}
                              data-testid={`try-image-${opt.id}`}
                              onClick={() => {
                                if (opt.locked) {
                                  toast.message("Image options are for subscribers", {
                                    description: "Upload your own photos or pick from our stock library — unlocks with the 14-day free trial.",
                                    action: { label: "Start free trial", onClick: () => navigate("/login") },
                                  });
                                }
                              }}
                              className={`relative w-full flex items-center gap-3 text-left py-2.5 px-3 rounded-lg border transition-all ${
                                opt.locked
                                  ? "border-slate-200 bg-slate-50 text-slate-400 hover:border-[#B7EB1B]/50 hover:bg-[#B7EB1B]/5 cursor-pointer"
                                  : isActive
                                    ? "border-[#0d9488] bg-[#0d9488]/10 text-[#0d9488]"
                                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                              }`}
                            >
                              <Icon className={`w-4 h-4 flex-shrink-0 ${opt.locked ? "text-slate-400" : isActive ? "text-[#0d9488]" : "text-slate-500"}`} />
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm leading-tight ${isActive && !opt.locked ? "font-semibold" : "font-medium"}`}>{opt.label}</div>
                                <div className="text-[11px] text-slate-400 truncate">{opt.desc}</div>
                              </div>
                              {opt.locked && <Lock className="w-3 h-3 text-slate-400 flex-shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Layout */}
                    <div className="mb-5">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block">Layout</Label>
                        <span className="text-[10px] text-slate-400 font-medium">1 free · 2 with subscription</span>
                      </div>
                      <div className="space-y-2">
                        {LAYOUT_OPTIONS.map((l) => {
                          const Icon = l.icon;
                          const isActive = layout === l.id;
                          return (
                            <button
                              key={l.id}
                              data-testid={`try-layout-${l.id}`}
                              onClick={() => {
                                if (l.locked) {
                                  toast.message("This layout is for subscribers", {
                                    description: "All 3 layouts unlock with the 14-day free trial — no credit card.",
                                    action: { label: "Start free trial", onClick: () => navigate("/login") },
                                  });
                                  return;
                                }
                                setLayout(l.id);
                                triggerRerender(theme, accentColor, l.id);
                              }}
                              className={`relative w-full flex items-center gap-3 text-left py-2.5 px-3 rounded-lg border transition-all ${
                                l.locked
                                  ? "border-slate-200 bg-slate-50 text-slate-400 hover:border-[#B7EB1B]/50 hover:bg-[#B7EB1B]/5 cursor-pointer"
                                  : isActive
                                    ? "border-[#0d9488] bg-[#0d9488]/10 text-[#0d9488]"
                                    : "border-slate-200 text-slate-600 hover:border-slate-300"
                              }`}
                            >
                              <Icon className={`w-4 h-4 flex-shrink-0 ${l.locked ? "text-slate-400" : isActive ? "text-[#0d9488]" : "text-slate-500"}`} />
                              <div className="flex-1 min-w-0">
                                <div className={`text-sm leading-tight ${isActive && !l.locked ? "font-semibold" : "font-medium"}`}>{l.label}</div>
                                <div className="text-[11px] text-slate-400 truncate">{l.desc}</div>
                              </div>
                              {l.locked && <Lock className="w-3 h-3 text-slate-400 flex-shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Theme */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide block">Theme</Label>
                        <span className="text-[10px] text-slate-400 font-medium">5 free · 8 with subscription</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {THEME_OPTIONS.map((t) => (
                          <button
                            key={t.id}
                            data-testid={`try-theme-${t.id}`}
                            onClick={() => {
                              if (t.locked) {
                                toast.message("This theme is for subscribers", {
                                  description: "8 industry themes unlock with the 14-day free trial — no credit card.",
                                  action: { label: "Start free trial", onClick: () => navigate("/login") },
                                });
                                return;
                              }
                              setTheme(t.id);
                              triggerRerender(t.id, accentColor);
                            }}
                            className={`relative text-sm py-2 px-3 rounded-lg border transition-all ${
                              t.locked
                                ? "border-slate-200 bg-slate-50 text-slate-400 hover:border-[#B7EB1B]/50 hover:bg-[#B7EB1B]/5 cursor-pointer"
                                : theme === t.id
                                  ? "border-[#0d9488] bg-[#0d9488]/10 text-[#0d9488] font-semibold"
                                  : "border-slate-200 text-slate-600 hover:border-slate-300"
                            }`}
                          >
                            {t.locked && (
                              <Lock className="w-3 h-3 absolute top-1.5 right-1.5 text-slate-400" />
                            )}
                            <span className={t.locked ? "pr-3" : ""}>{t.label}</span>
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate("/login")}
                        className="mt-3 text-xs text-[#0d9488] hover:text-[#0d9488]/80 font-semibold inline-flex items-center gap-1"
                        data-testid="try-unlock-themes-link"
                      >
                        <Sparkles className="w-3 h-3" /> Unlock all 13 themes →
                      </button>
                    </div>
                  </CardContent>
                </Card>

                {/* Soft conversion: send to yourself */}
                <Card className="rounded-2xl border-[#B7EB1B]/40 bg-gradient-to-br from-[#B7EB1B]/10 to-white">
                  <CardContent className="p-5">
                    <Send className="w-6 h-6 text-[#0d9488] mb-3" />
                    <h3 className="font-bold text-slate-900 mb-1" style={{fontFamily: 'Manrope'}}>Like it? Email it to yourself.</h3>
                    <p className="text-sm text-slate-600 mb-4">See exactly how it'll look in your inbox.</p>
                    <Button
                      data-testid="try-send-self-btn"
                      onClick={() => setShowSendModal(true)}
                      className="w-full bg-[#0d9488] hover:bg-[#0d9488]/90 text-white rounded-full"
                    >
                      <Send className="w-4 h-4 mr-2" /> Send to my email
                    </Button>
                  </CardContent>
                </Card>

                <Button
                  data-testid="try-reset-btn"
                  variant="ghost"
                  onClick={resetDemo}
                  className="w-full text-slate-500 hover:text-slate-700 text-sm"
                >
                  <RefreshCcw className="w-3 h-3 mr-2" /> Try another URL
                </Button>
              </aside>

              {/* Preview */}
              <main className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-[#B7EB1B]/40">
                  {/* DEMO PREVIEW stripe */}
                  <div className="bg-gradient-to-r from-[#B7EB1B] to-[#a8d818] text-[#0a1e3a] px-4 py-2 flex items-center justify-between text-xs font-bold tracking-wide">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5" />
                      DEMO PREVIEW — Not Sent
                    </div>
                    <span className="hidden sm:inline font-normal text-[#0a1e3a]/70">
                      Sign up to send to your contacts →
                    </span>
                  </div>
                  <div className="border-b border-slate-100 p-4 bg-slate-50">
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Subject</div>
                    <div className="text-slate-900 font-semibold">{result.subject}</div>
                  </div>
                  <div className="overflow-y-auto max-h-[80vh] relative">
                    <iframe
                      data-testid="try-email-preview"
                      title="Email preview"
                      srcDoc={result.html_content}
                      className="w-full"
                      style={{height: 800, border: 0}}
                    />
                  </div>
                </div>
                {/* What you unlock with subscription — prominent showcase */}
                <div
                  data-testid="try-locked-features"
                  className="relative mt-6 rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#0a1e3a] to-[#003858]"
                >
                  <div className="relative p-7 sm:p-9 text-white">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-1">
                      <div className="w-9 h-9 bg-[#B7EB1B] rounded-xl flex items-center justify-center shadow-lg shadow-[#B7EB1B]/30">
                        <Sparkles className="w-5 h-5 text-[#0a1e3a]" />
                      </div>
                      <Badge className="bg-white/10 backdrop-blur text-[#B7EB1B] border-[#B7EB1B]/30 font-semibold tracking-wide text-[10px] uppercase">
                        What you unlock
                      </Badge>
                    </div>
                    <h3 className="text-3xl sm:text-4xl font-extrabold mt-3 mb-2 leading-tight" style={{fontFamily: 'Manrope'}}>
                      The full, simple to use, toolkit <br className="hidden sm:block" />
                      <span className="text-[#B7EB1B]">when you're ready.</span>
                    </h3>
                    <p className="text-white/70 text-base mb-7 max-w-lg">
                      You've seen the demo. Here's everything that opens up the moment you start your 14-day free trial.
                    </p>

                    {/* Feature grid */}
                    <div className="grid sm:grid-cols-2 gap-3 mb-7">
                      {[
                        { icon: Users,              title: "Send to your contact list",      desc: "Bulk send to your subscribers" },
                        { icon: ImageIcon,          title: "Image library & uploads",        desc: "Swap in your own photos" },
                        { icon: Edit3,              title: "Edit any text or section",       desc: "Tweak headlines, copy, CTAs" },
                        { icon: MousePointerClick,  title: "Open & click tracking",          desc: "See who opened and clicked" },
                        { icon: CalendarClock,      title: "Email scheduling",               desc: "Send now or pick a date" },
                        { icon: Palette,            title: "13 industry-specific themes",    desc: "Real estate, restaurant & more" },
                      ].map(({ icon: Icon, title, desc }) => (
                        <div
                          key={title}
                          className="flex items-start gap-3 p-3.5 rounded-2xl bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 transition-colors"
                        >
                          <div className="w-10 h-10 rounded-xl bg-[#B7EB1B]/15 border border-[#B7EB1B]/30 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-[#B7EB1B]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm leading-tight text-white" style={{fontFamily: 'Manrope'}}>{title}</div>
                            <div className="text-xs text-white/60 mt-0.5">{desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4 border-t border-white/10">
                      <Button
                        data-testid="try-unlock-btn"
                        onClick={() => navigate("/login")}
                        className="bg-[#B7EB1B] hover:bg-[#B7EB1B]/90 text-[#0a1e3a] rounded-full font-bold h-12 px-7 text-base shadow-lg shadow-[#B7EB1B]/20"
                      >
                        Start 14-day Free Trial <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                      <div className="text-xs text-white/60 leading-relaxed">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#B7EB1B] inline mr-1 -mt-0.5" />
                        No credit card required ·
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#B7EB1B] inline mx-1 -mt-0.5" />
                        Cancel anytime ·
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#B7EB1B] inline mx-1 -mt-0.5" />
                        60-second setup
                      </div>
                    </div>
                  </div>
                </div>
              </main>
            </div>

            {/* Sister tools cross-link — only after demo generated, kept lightweight */}
            <div className="max-w-7xl mx-auto px-6 mt-10" data-testid="try-sister-tools">
              <div className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
                  <div>
                    <div className="text-xs font-semibold tracking-wide uppercase text-[#0d9488] mb-1">
                      <Sparkles className="w-3 h-3 inline mr-1" /> Better together
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                      The rest of the Simply Smarter suite
                    </h3>
                  </div>
                  <Link
                    to="/suite"
                    className="text-sm font-semibold text-slate-500 hover:text-[#0d9488] inline-flex items-center"
                    data-testid="try-see-all-tools"
                  >
                    See all tools <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Link>
                </div>

                <div className="grid md:grid-cols-3 gap-3">
                  <a
                    href="https://brand-report-r-1773343747.emergent.host/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="try-cross-scorecard"
                    className="group flex items-center gap-4 p-4 rounded-2xl border border-slate-200 hover:border-[#B7EB1B] hover:bg-slate-50 transition-all"
                  >
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0a1e3a] to-[#003858] flex items-center justify-center flex-shrink-0">
                      <BarChart3 className="w-5 h-5 text-[#B7EB1B]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-900 text-sm" style={{fontFamily: 'Manrope'}}>Scorecard</span>
                        <Badge className="bg-[#B7EB1B]/20 text-[#0d9488] border-[#B7EB1B]/30 text-[10px] px-2 py-0">FREE</Badge>
                      </div>
                      <p className="text-xs text-slate-600 mt-0.5">2-minute brand audit across Google, social &amp; maps.</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#0d9488] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                  </a>

                  <a
                    href="https://consumer-check-1.preview.emergentagent.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="try-cross-questionnaire"
                    className="group flex items-center gap-4 p-4 rounded-2xl border border-slate-200 hover:border-[#B7EB1B] hover:bg-slate-50 transition-all"
                  >
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#006080] to-[#0d9488] flex items-center justify-center flex-shrink-0">
                      <MessageSquareQuote className="w-5 h-5 text-[#B7EB1B]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-900 text-sm" style={{fontFamily: 'Manrope'}}>Customer Questionnaire</span>
                        <Badge className="bg-[#B7EB1B]/20 text-[#0d9488] border-[#B7EB1B]/30 text-[10px] px-2 py-0">FREE</Badge>
                      </div>
                      <p className="text-xs text-slate-600 mt-0.5">Find out where your customers actually spend their media time.</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#0d9488] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                  </a>

                  <a
                    href="https://blog-factory-6.preview.emergentagent.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="try-cross-blog"
                    className="group flex items-center gap-4 p-4 rounded-2xl border border-slate-200 hover:border-[#B7EB1B] hover:bg-slate-50 transition-all"
                  >
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#119AB3] to-[#003858] flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-[#B7EB1B]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-900 text-sm" style={{fontFamily: 'Manrope'}}>Blog Posts</span>
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px] px-2 py-0">$49/MO</Badge>
                      </div>
                      <p className="text-xs text-slate-600 mt-0.5">AI writes blog posts in your voice. Each doubles as a newsletter.</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#0d9488] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Send-test modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur z-[100] flex items-center justify-center p-4">
          <Card className="w-full max-w-md rounded-3xl" data-testid="try-send-modal">
            <CardContent className="p-6">
              <Mail className="w-10 h-10 text-[#0d9488] mb-3" />
              <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>Send this to your inbox</h3>
              <p className="text-slate-600 text-sm mb-5">
                We'll email you the newsletter — exactly as your recipients would see it.
              </p>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Your email</Label>
              <Input
                data-testid="try-test-email-input"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@yourbusiness.com"
                className="h-12 rounded-xl mb-4"
              />
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setShowSendModal(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="try-send-confirm-btn"
                  onClick={handleSendTest}
                  disabled={sendingTest}
                  className="flex-1 bg-[#0d9488] hover:bg-[#0d9488]/90 text-white rounded-full"
                >
                  {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send it
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-3 text-center">
                We won't add you to any list. One-time send to preview your demo.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Empty state — no result yet, show a "what to expect" tease */}
      {!result && !generating && (
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-6 grid sm:grid-cols-3 gap-6 text-center">
            {[
              { n: "01", t: "Paste any URL", d: "Blog post, news article, even your existing website" },
              { n: "02", t: "AI writes & designs", d: "Subject line, headlines, copy, layout — all drafted in seconds" },
              { n: "03", t: "Customize the colors", d: "See it re-render instantly with your brand color" },
            ].map((s) => (
              <div key={s.n}>
                <div className="text-[#0d9488] font-mono text-sm mb-2">{s.n}</div>
                <h3 className="font-bold text-slate-900 mb-2" style={{fontFamily: 'Manrope'}}>{s.t}</h3>
                <p className="text-sm text-slate-600">{s.d}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-[#003858] text-white py-10 mt-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/brand/afa-logo-pin.png" alt="Anne Flinn Advertising" className="w-8 h-8" />
            <div className="text-sm text-white/60">
              © {new Date().getFullYear()} Anne Flinn Advertising, LLC · <span className="text-[#B7EB1B]">Advertise Smarter with Us</span>
            </div>
          </div>
          <div className="text-xs text-white/40">Email infrastructure powered by Swiftlett</div>
        </div>
      </footer>
    </div>
  );
}
